import { ArrowBack, Delete as DeleteIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { TERMINATION_REASON_CODES } from '../../utils/constants';

interface TerminationReviewTableProps {
  rows: TerminationRow[];
  onBack: () => void;
  onValidate: () => void;
  onNext: () => void;
  onDeleteRow: (index: number) => void;
  isValidating: boolean;
  isValidated: boolean;
}

function statusChip(
  row: TerminationRow,
  isValidated: boolean
): { label: string; color: ChipProps['color'] } {
  if (row.errors?.validation) return { label: 'Error', color: 'error' };
  if (row.errors?.warning) return { label: 'Warning', color: 'warning' };
  if (!isValidated) return { label: 'Pending', color: 'default' };
  return { label: 'Ready', color: 'success' };
}

const TerminationReviewTable: React.FC<TerminationReviewTableProps> = ({
  rows,
  onBack,
  onValidate,
  onNext,
  onDeleteRow,
  isValidating,
  isValidated,
}) => {
  const gridRows = useMemo(() => rows.map((r, i) => ({ ...r, id: i })), [rows]);

  const summary = useMemo(() => {
    const errors = rows.filter((r) => r.errors?.validation).length;
    const warnings = rows.filter(
      (r) => r.errors?.warning && !r.errors?.validation
    ).length;
    const ready = rows.filter((r) => !r.errors && !r.submitted).length;
    return { total: rows.length, ready, warnings, errors };
  }, [rows]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'employee_id',
        headerName: 'Employee ID',
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'term_status_code',
        headerName: 'Status Code',
        flex: 0.8,
        minWidth: 80,
      },
      {
        field: 'reason_code',
        headerName: 'Reason',
        flex: 1.2,
        minWidth: 160,
        valueFormatter: (params) => {
          const code = params.value as string;
          return (
            TERMINATION_REASON_CODES[
              code as keyof typeof TERMINATION_REASON_CODES
            ] ?? code
          );
        },
      },
      {
        field: 'term_date',
        headerName: 'Term Date',
        flex: 0.9,
        minWidth: 90,
        valueFormatter: (params) => {
          const value = params.value as string;
          if (!value) return '';
          try {
            return format(parseISO(value), 'MM-dd-yyyy');
          } catch {
            return value;
          }
        },
      },
      { field: 'turn_off_ach', headerName: 'ACH Off', flex: 0.6, minWidth: 60 },
      {
        field: 'term_reason',
        headerName: 'Term Reason',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Box
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', py: 0.5 }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'last_day_worked',
        headerName: 'Last Day Worked',
        flex: 0.9,
        minWidth: 110,
        valueFormatter: (params) => {
          const value = params.value as string;
          if (!value) return '';
          try {
            return format(parseISO(value), 'MM-dd-yyyy');
          } catch {
            return value;
          }
        },
      },
      {
        field: 'status',
        headerName: 'Validation',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TerminationRow>) => {
          const chip = statusChip(params.row, isValidated);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={
                  params.row.errors
                    ? Object.values(params.row.errors).join('; ')
                    : ''
                }
                arrow
              >
                <Chip
                  label={chip.label}
                  color={chip.color}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<TerminationRow>) => (
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={() => onDeleteRow(params.id as number)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [onDeleteRow, isValidated]
  );

  return (
    <Box>
      {/* Header + summary */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Review Terminations
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Verify each row before submitting. Remove any rows you don't want to
            process.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {[
            { value: summary.total, label: 'Total', color: 'text.primary' },
            { value: summary.ready, label: 'Ready', color: 'success.main' },
            {
              value: summary.warnings,
              label: 'Warnings',
              color: summary.warnings > 0 ? 'warning.main' : 'text.primary',
            },
            {
              value: summary.errors,
              label: 'Errors',
              color: summary.errors > 0 ? 'error.main' : 'text.primary',
            },
          ].map((s, i) => (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && (
                <Box
                  sx={{
                    width: '1px',
                    height: 32,
                    backgroundColor: 'divider',
                    mr: 3,
                  }}
                />
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: s.color }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {summary.errors > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 2, py: 0.5, borderRadius: 1 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {summary.errors} row(s) have errors — remove before submitting
          </Typography>
        </Alert>
      )}

      {/* DataGrid */}
      <DataGrid
        rows={gridRows}
        columns={columns}
        loading={isValidating}
        density="compact"
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => (params.row.errors ? 'row--error' : '')}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          height: 500,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '& .MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          '& .row--error': {
            backgroundColor: 'error.lighter',
            borderLeft: '3px solid',
            borderLeftColor: 'error.main',
          },
        }}
      />

      {/* Actions */}
      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onBack} variant="outlined" startIcon={<ArrowBack />}>
            Back to Upload
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LoadingButton
            variant={isValidated ? 'outlined' : 'contained'}
            onClick={onValidate}
            disabled={rows.length === 0}
            loading={isValidating}
            size="large"
          >
            {isValidated
              ? 'Re-validate'
              : `Validate ${rows.length} Employee${
                  rows.length !== 1 ? 's' : ''
                }`}
          </LoadingButton>
          <Button
            variant="contained"
            onClick={onNext}
            disabled={!isValidated || summary.errors > 0 || isValidating}
            size="large"
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TerminationReviewTable;
